import React, { useState, useEffect } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  Card,
  TextAction,
} from '@leagueplatform/genesis-core';
import { getPartnerAccessToken } from '@leagueplatform/sso-util';
import { useStaticAsset } from '@leagueplatform/asset-config';
import {
  AppEnvironment,
  ENVIRONMENT,
  useConfigProperty,
} from '@leagueplatform/core';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { cobRoutesMap } from '@leagueplatform/engen-capability-cob-library';
import {
  MedicationForm,
  StepTherapyForm,
  DentalClaimForm,
  DirectReimbursementForm,
} from './forms';
import { TabsComponent } from './tabs-component/tabs.component';
import {
  FORM_TYPE,
  FORM_OPEN_TYPE,
  HIGHMARK_APP_TYPE,
} from '../types/online-forms.types';
import {
  emptyStorage,
  openPrefilledPdf,
  getAccessToken,
} from './forms/helpers';
import { ErrorModal } from './forms/layout/errorModal';
import { formsLibrary } from '../forms-library.config';
import { FORMS_LIBRARY_ASSET_KEYS } from '../types/forms-library.types';
import { NoFormAvailable } from './forms/layout/noFormAvailable';

export function FormsLibraryComponent() {
  const navigate = useNavigate();
  const tenantId = useConfigProperty('core.tenantId');
  const [showForm, setShowForm] = useState('');
  const [errMsg, setErrMsg] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const [accessToken, setAccesstoken] = useState('');
  const [groupNumberFromChild, setGroupNumberFromChild] = useState('');
  const [formsFromChild, setFormsFromChild] = useState({});
  const [loadUbk, setLoadUBK] = useState<any>();

  const handleGroupNumberFromChild = (groupNumber: string) => {
    setGroupNumberFromChild(groupNumber);
  };

  const handleFormsFromChild = (forms: Object) => {
    setFormsFromChild(forms);
  };

  const leftArrowBlackIcon = useStaticAsset(
    FORMS_LIBRARY_ASSET_KEYS.LEFT_ARROW_BLACK_ICON,
  );

  const config = formsLibrary.getConfig();
  const { HIGHMARK_API_URL, useCurrentRegionUbkHeader } = config;
  const currentUbk: any = useCurrentRegionUbkHeader() || null;
  const ubkValue = currentUbk?.headers?.Brandubk || null;

  const getPartnerToken = async () => {
    try {
      const partnerAccessToken = await getPartnerAccessToken('highmark');
      setAccesstoken(partnerAccessToken);
    } catch (error) {
      throw new Error('Error getting partneraccess token');
    }
  };

  function setCookie(c_name: any) {
    document.cookie = c_name;
  }

  function getCookie(name: string) {
    const cookieValue: any = {};
    document.cookie.split(';').forEach((el) => {
      const split = el.split('=');
      cookieValue[split[0].trim()] = split.slice(1).join('=');
    });
    return cookieValue[name];
  }

  useEffect(() => {
    const UBKBrand = `BrandUBK= ${ubkValue}`;
    if (ubkValue !== undefined && ubkValue !== '' && ubkValue !== null) {
      setLoadUBK(ubkValue);
      setCookie(UBKBrand);
    } else {
      setLoadUBK(getCookie('BrandUBK'));
    }
    getPartnerToken();
  }, [ubkValue]);

  useEffect(() => {
    sessionStorage.removeItem('planDetailsKeys');
  }, []);

  function getUrl(formUrl: any) {
    let baseUrl;
    if (AppEnvironment.environment === ENVIRONMENT.PRODUCTION) {
      baseUrl = 'https://cdn.highmark.com';
      if (tenantId === HIGHMARK_APP_TYPE.AMH) {
        baseUrl = 'https://cdn.allmyhealth.com';
      }
    } else {
      baseUrl = 'https://test.cdn.highmark.com';
      if (tenantId === HIGHMARK_APP_TYPE.AMH) {
        baseUrl = 'https://test.cdn.allmyhealth.com';
      }
    }
    const staticFormUrl = `${baseUrl}${formUrl}`;
    return staticFormUrl;
  }

  const displayPreFillPdf = async (formsChild: any, formUrl: string) => {
    const formsArray = formsChild.forms;
    setIsPdfLoading(true);
    const prefillFormPromises = formsArray
      .filter(
        (form: any) =>
          form.forms[0].url === formUrl && form.forms[0].prefillInfo.available,
      )
      .map(async (form: any) => {
        const PREFILL_PDF_URL = `${HIGHMARK_API_URL}/digital/v1/members/forms-library/prefill-pdf?formName=${form.forms[0].prefillInfo.prefillFormName}&groupNumber=${groupNumberFromChild}&brand=${loadUbk}`;
        try {
          const res = await getAccessToken(
            PREFILL_PDF_URL,
            loadUbk,
            '',
            accessToken,
          );
          const { status } = res;
          setIsPdfLoading(false);
          if (status === 200) {
            openPrefilledPdf(res.content);
          }
          if (res?.errMsg) {
            setIsPdfLoading(false);
          }
        } catch (error) {
          throw new Error('Error getting pre-fill PDF content');
        }
      });
    await Promise.all(prefillFormPromises);
  };

  const openForm = (
    formType: string,
    formUrl: string,
    identifier: string,
    prefillInfo?: any,
  ) => {
    if (formType === FORM_OPEN_TYPE.ONLINE_FORM) {
      switch (identifier) {
        case FORM_TYPE.MEDICATION:
          setShowForm('medication');
          break;
        case FORM_TYPE.STEP_THERAPY:
          setShowForm('stepTherapy');
          break;
        case FORM_TYPE.DENTAL:
          setShowForm('dentalClaim');
          break;
        case FORM_TYPE.DIRECT_REIMBURSEMENT:
          setShowForm('directReimbursement');
          break;
        case FORM_TYPE.COB:
          navigate(cobRoutesMap.cob);
          break;
        default:
          break;
      }
    } else if (formType === FORM_OPEN_TYPE.PDF_FORM) {
      const staticFormUrl = getUrl(formUrl);
      if (prefillInfo?.available) {
        displayPreFillPdf(formsFromChild, formUrl);
      } else {
        setIsPdfLoading(true);
        window.open(staticFormUrl, '_blank');
        setIsPdfLoading(false);
      }
    }
  };

  const closeForm = () => {
    setShowForm('');
    emptyStorage();
    setErrMsg(null);
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Box
      css={
        {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: !showForm ? '#ffffff' : '#F8F8F8',
          padding: !showForm ? '' : '$one',
        } as GDSStyleObject
      }
    >
      {errMsg && <ErrorModal onClose={closeForm} />}

      {!showForm && !errMsg && (
        <>
          <Card.Flat
            css={
              {
                backgroundColor: '#EAF5FD',
                height: '152px',
              } as GDSStyleObject
            }
          >
            <Card.Section
              css={
                {
                  boxSizing: 'border-box',
                  margin: '0px auto',
                  width: '100%',
                  maxWidth: '1132px',
                  '@mobile': { paddingLeft: '25px' },
                  '@tablet': { paddingLeft: '25px' },
                  '@mobileLandscape': { paddingLeft: '25px' },
                  '@laptop': { padding: '0px 79px' },
                  '@desktop': { padding: '0px 79px' },
                } as GDSStyleObject
              }
            >
              <Box
                css={
                  {
                    display: 'flex',
                    marginTop: '15px',
                    flexDirection: 'row',
                    '@mobile': { flexDirection: 'column' },
                    '@tablet': { flexDirection: 'column' },
                    '@mobileLandscape': { flexDirection: 'column' },
                  } as GDSStyleObject
                }
              >
                <TextAction onClick={goBack} css={{ width: '24px' }}>
                  <img
                    src={leftArrowBlackIcon as string}
                    alt="Back Button"
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </TextAction>
                <Box
                  css={{
                    display: 'inline-flex',
                    height: '110px',
                    '@laptop': { paddingLeft: '22px' },
                    '@desktop': { paddingLeft: '22px' },
                  }}
                >
                  <HeadingText
                    level="1"
                    size={{
                      '@initial': 'xxl',
                      '@laptop': 'xxl',
                      '@desktop': 'xxl',
                      '@mobile': 'xxl',
                      '@mobileLandscape': 'xxl',
                      '@tablet': 'xxl',
                    }}
                    css={{
                      position: 'relative',
                      '@mobile': { paddingLeft: '0', top: '10px' },
                      '@tablet': { paddingLeft: '0', top: '10px' },
                      '@mobileLandscape': { paddingLeft: '0', top: '10px' },
                      '@laptop': { paddingLeft: '20', top: '32px' },
                      '@desktop': { paddingLeft: '20', top: '32px' },
                    }}
                  >
                    Forms Library
                  </HeadingText>
                </Box>
              </Box>
            </Card.Section>
          </Card.Flat>
          {loadUbk === undefined || loadUbk === null ? (
            <NoFormAvailable />
          ) : (
            <TabsComponent
              openForm={openForm}
              ubk={loadUbk}
              closeForm={closeForm}
              onGroupNumberChange={handleGroupNumberFromChild}
              onFormsChange={handleFormsFromChild}
              isPdfLoading={isPdfLoading}
              accessToken={accessToken}
            />
          )}
        </>
      )}
      {showForm === 'medication' && (
        <MedicationForm closeForm={closeForm} accessToken={accessToken} />
      )}
      {showForm === 'stepTherapy' && (
        <StepTherapyForm closeForm={closeForm} accessToken={accessToken} />
      )}
      {showForm === 'dentalClaim' && (
        <DentalClaimForm
          closeForm={closeForm}
          ubk={loadUbk}
          accessToken={accessToken}
        />
      )}
      {showForm === 'directReimbursement' && (
        <DirectReimbursementForm
          closeForm={closeForm}
          accessToken={accessToken}
        />
      )}
    </Box>
  );
}
